import * as React from 'react';
import { getConfig } from '@cian/config/browser';
import { getHttpApi } from '@cian/http-api/browser';
import { getLogger } from '@cian/logger/browser';
import { registry as getRegistry } from '@cian/mf-registry/browser';
import { getTelemetry } from '@cian/telemetry/browser';
import { hydrateRoot } from 'react-dom/client';
import { toError } from '../../../shared/utils/toError';
import { SsrMarkupFallback } from '../../../shared/components/SsrMarkupFallback';
import { getEventQueue } from '../../../shared/utils/eventQueue';
import { Application } from '../../../shared/containers/App';
import { ICustomContextParams } from '../../../shared/types/ICustomContextParams';
import { TReduxStore } from '../../../shared/types/redux';

interface IDependencies {
  store: TReduxStore;
}

export function renderApplication(dependencies: IDependencies) {
  const config = getConfig();
  const httpApi = getHttpApi();
  const logger = getLogger();
  const microfrontendsRegistry = getRegistry();
  const telemetry = getTelemetry();
  const eventQueue = getEventQueue();

  const { store } = dependencies;

  const projectName = config.getStrict<string>('projectName');
  const customContext = config.getStrict<ICustomContextParams>('applicationContext.custom');
  const rootElement = document.getElementById(projectName as string);

  if (!rootElement) {
    throw new Error(`Unable to find element #${projectName}`);
  }

  /**
   * Снимок серверной разметки до гидрации. React 19 при ошибке гидрации очищает root
   * и перерендеривает его на клиенте, поэтому снимок можно взять только здесь.
   * Используется как заглушка корневого error boundary и для восстановления после uncaught error.
   */
  const ssrMarkup = rootElement.innerHTML;

  hydrateRoot(
    rootElement,
    <Application
      eventQueue={eventQueue}
      config={config}
      logger={logger}
      httpApi={httpApi}
      reduxStore={store}
      telemetry={telemetry}
      customContext={customContext}
      microfrontendsRegistry={microfrontendsRegistry}
      errorStub={<SsrMarkupFallback html={ssrMarkup} />}
    />,
    {
      // Ошибка поймана error boundary (корневым или локальным). Алерты настраиваются по domain.
      onCaughtError: (error, errorInfo) => {
        logger.error(toError(error), { domain: 'react.caughtError', componentStack: errorInfo.componentStack });
      },
      // Ошибка не поймана ни одним boundary: React размонтировал root. Возвращаем серверную разметку,
      // чтобы страница не осталась пустой. Пользователь увидит откат к стартовому состоянию, для SEO это норма.
      onUncaughtError: (error, errorInfo) => {
        logger.error(toError(error), { domain: 'react.uncaughtError', componentStack: errorInfo.componentStack });
        rootElement.innerHTML = ssrMarkup;
      },
      // Hydration mismatch и ошибки гидрации, после которых React сам перерендерил корень на клиенте.
      onRecoverableError: (error, errorInfo) => {
        logger.warning(toError(error), {
          domain: 'react.recoverableError',
          componentStack: errorInfo.componentStack,
        });
      },
    },
  );
}
